import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axios from 'utils/axios';
import AxiosAdmin from 'utils/axiosAdmin';
import URL from 'config/url';

const formatQueryParam = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.length > 0 ? value.map(String).join(',') : '';
  return String(value);
};

export const buildFlightBookingsQuery = (data, options = {}) => {
  const { csv = false } = options;
  const params = new URLSearchParams();

  params.set('page', String(data.page || 1));
  params.set('limit', String(data.limit || 10));
  if (data.pageSize !== undefined && data.pageSize !== null) {
    params.set('page_size', String(data.pageSize));
  }
  if (csv) params.set('csv', 'true');
  if (data.id) params.set('id', data.id);

  params.set('booking_start_date', data.booking_start_date ?? data.startDate ?? '');
  params.set('booking_end_date', data.booking_end_date ?? data.endDate ?? '');
  params.set('travel_start_date', data.travel_start_date ?? data.travelStartDate ?? '');
  params.set('travel_end_date', data.travel_end_date ?? data.travelEndDate ?? '');
  params.set('booking_status', formatQueryParam(data.booking_status ?? data.bookingStatus));
  params.set('provider_name', formatQueryParam(data.provider_name ?? data.consolidator ?? data.provider));
  params.set('currency', formatQueryParam(data.currency));
  params.set('account_name', formatQueryParam(data.account_name ?? data.agencyName));
  params.set('account_id', formatQueryParam(data.account_id ?? data.agencyId));
  params.set('text', data.text ?? '');

  const sortingField = data.sorting_field ?? data.sortingField;
  if (sortingField) params.set('sorting_field', sortingField);

  const sorting = data.sorting;
  if (sorting === 'ascend') params.set('sorting', 'asc');
  else if (sorting === 'descend') params.set('sorting', 'desc');
  else if (sorting === 'asc' || sorting === 'desc') params.set('sorting', sorting);

  params.set('payment_gateway', formatQueryParam(data.payment_gateway ?? data.paymentGateway));
  params.set('payment_type', formatQueryParam(data.payment_method_type ?? data.paymentMethodTypes));
  params.set('user_type', formatQueryParam(data.user_type ?? data.userType));
  params.set('cogs', formatQueryParam(data.cogs));
  params.set('non_login', formatQueryParam(data.non_login ?? data.isNonLogin));
  params.set('cogs_paid_date', data.cogs_paid_date ?? data.cogsPaidDate ?? '');
  params.set('agency_commission_paid', formatQueryParam(data.agency_commission_paid ?? data.agencyCommissionPaid));
  params.set('agency_commission_paid_date', data.agency_commission_paid_date ?? data.agencyCommissionPaidDate ?? '');
  params.set('vendor_cogs_payment_mode', formatQueryParam(data.vendor_cogs_payment_mode ?? data.cogsPaymentMode));
  params.set('aggregator', formatQueryParam(data.aggregator ?? data.vendor));

  return params.toString();
};

const resolveFlightBookingId = async (data, thunkAPI) => {
  const bookingId = data.booking_id ?? data.id ?? data.referenceNumber;
  if (bookingId) return bookingId;

  const referenceNumber = data.reference_number ?? data.referenceNumber;
  if (!referenceNumber) return undefined;

  const response = await AxiosAdmin.get(`${URL.HOTEL_BOOKING_DETAILS_V2}/${referenceNumber}`, thunkAPI);
  const details = response.data?.data;
  const bookingRecord = details?.bookings?.[0] || details;
  return bookingRecord?.booking_id ?? bookingRecord?.id;
};

export const getFlights = createAsyncThunk('GET FLIGHTS', async (data, thunkAPI) => {
  const query = buildFlightBookingsQuery(data);
  const response = await AxiosAdmin.get(`${URL.FLIGHT}?${query}`, thunkAPI);
  return response.data;
});

export const getFlightDetails = createAsyncThunk('GET FLIGHTS DETAILS', async (data, thunkAPI) => {
  const response = await AxiosAdmin.get(`${URL.FLIGHT_DETAILS}/${data.referenceNumber}`, thunkAPI);
  return response.data;
});

export const resendEmailConfirmation = createAsyncThunk(
  'RESEND_FLIGHT_EMAIL_CONFIRMATION',
  async ({ requestData, v2Version }, thunkAPI) => {
    let reqData = {
      reference_number: requestData.bookingId,
      email: requestData.email,
    };
    const response = await AxiosAdmin.post(URL.RESEND_FLIGHT_CONFIRMATION_EMAIL_V2, reqData, thunkAPI);
    return response.data;
    
  }
);

export const updateFlightRemark = createAsyncThunk('UPDATE_FLIGHT_REMARKS', async (data, thunkAPI) => {
  const bookingId = await resolveFlightBookingId(data, thunkAPI);
  const requestData = {
    remark_id: data?.remark_id || '',
    remark: data?.remark,
  };
  const response = await AxiosAdmin.patch(`${URL.FLIGHT_UPDATE}/${bookingId}`, requestData, thunkAPI);
  return response.data;
});

export const updateFlightBooking = createAsyncThunk('UPDATE_FLIGHT_BOOKING', async (data, thunkAPI) => {
  const bookingId = await resolveFlightBookingId(data, thunkAPI);
  const { booking_id, id, referenceNumber, reference_number, ...payload } = data;
  const response = await AxiosAdmin.patch(`${URL.FLIGHT_UPDATE}/${bookingId}`, payload, thunkAPI);
  return response.data;
});

const initialState = {
  isLoading: false,
  flightData: null,
  flightDetails: null,
};

export const flightSlice = createSlice({
  name: 'flight',
  initialState,
  reducers: {},
  extraReducers: {
    // get flights
    [getFlights.pending]: (state) => {
      state.isLoading = true;
      state.flightData = null;
    },
    [getFlights.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.flightData = action.payload.data;
    },
    [getFlights.rejected]: (state, action) => {
      state.isLoading = false;
      state.flightData = null;
      toast.error(action?.payload?.message);
    },

    // update flight remarks
    [updateFlightRemark.pending]: (state) => {
      state.isLoading = true;
    },
    [updateFlightRemark.fulfilled]: (state) => {
      state.isLoading = false;
      toast.success('Remarks updated successfully');
    },
    [updateFlightRemark.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action.payload?.message || 'Error updating remarks');
    },
    // get flight Details
    [getFlightDetails.pending]: (state) => {
      state.isLoading = true;
      state.flightDetails = null;
    },
    [getFlightDetails.fulfilled]: (state, action) => {
      state.isLoading = false;
      const payload = action.payload;
      const details = payload?.data ?? payload;
      state.flightDetails = details?.bookings?.[0] || details;
    },
    [getFlightDetails.rejected]: (state, action) => {
      state.isLoading = false;
      state.flightDetails = null;
      toast.error(action?.payload?.message);
    },

    // resend hotel confirmation email
    [resendEmailConfirmation.pending]: (state) => {
      state.isLoading = true;
    },
    [resendEmailConfirmation.fulfilled]: (state) => {
      state.isLoading = false;
      // Handle success, e.g., show a success toast message
      toast.success('Email confirmation resent successfully');
    },
    [resendEmailConfirmation.rejected]: (state, action) => {
      state.isLoading = false;
      // Handle error, e.g., show an error toast message
      toast.error(action.payload?.error || 'Error resending email confirmation');
    },

    // update hotel booking
    [updateFlightBooking.pending]: (state) => {
      state.isLoading = true;
    },
    [updateFlightBooking.fulfilled]: (state, action) => {
      state.isLoading = false;
      toast.success(action.payload?.message);
    },
    [updateFlightBooking.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action.payload?.message || 'Error updating cogs data');
    },
  },
});

export default flightSlice.reducer;
